import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box, TableRow, TableCell, Typography, TextField, Pagination,
  DialogContent, Chip, useTheme, InputAdornment,
} from '@mui/material';
import { Search, Timeline as TimelineIcon } from '@mui/icons-material';
import {
  GlassCard, GlassTable, GlassDialog, ShimmerSkeleton, EmptyState,
} from '../../../components/admin/ui';
import { makeAnalyticsApi } from '../../../analytics/adminApi';
import { ADMIN_COLORS } from '../../../components/admin/ui/glassTokens';

const PAGE_SIZE = 50;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function roleBadgeColor(role) {
  switch (role) {
    case 'student': return ADMIN_COLORS.accentCyan;
    case 'librarian': return ADMIN_COLORS.accentViolet;
    case 'admin': return ADMIN_COLORS.danger;
    default: return ADMIN_COLORS.warning;
  }
}

// ─── Timeline item renderer ───────────────────────────────────────────────────

function TimelineItem({ item, isDark }) {
  const typeColor = {
    pageview: ADMIN_COLORS.accentIndigo,
    event: ADMIN_COLORS.success,
    booking: ADMIN_COLORS.warning,
    payment: ADMIN_COLORS.accentCyan,
  }[item.type] || ADMIN_COLORS.accentIndigo;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        py: 1.25,
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.05)'}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {/* Dot */}
      <Box
        sx={{
          flexShrink: 0,
          width: 10,
          height: 10,
          mt: 0.5,
          borderRadius: '50%',
          background: typeColor,
          boxShadow: `0 0 6px ${typeColor}88`,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={item.type}
            size="small"
            sx={{
              bgcolor: `${typeColor}22`,
              color: typeColor,
              fontWeight: 700,
              fontSize: '0.65rem',
              height: 18,
              border: 'none',
            }}
          />
          <Typography
            variant="body2"
            sx={{ color: isDark ? 'rgba(255,255,255,0.8)' : 'rgba(15,23,42,0.8)', fontSize: '0.83rem' }}
          >
            {item.label}
          </Typography>
        </Box>
        {item.meta && typeof item.meta === 'object' && (
          <Typography
            variant="caption"
            sx={{
              color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)',
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              display: 'block',
              mt: 0.25,
            }}
          >
            {JSON.stringify(item.meta).slice(0, 120)}
          </Typography>
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{
          flexShrink: 0,
          color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(15,23,42,0.35)',
          fontSize: '0.7rem',
          whiteSpace: 'nowrap',
        }}
      >
        {fmtDate(item.ts)}
      </Typography>
    </Box>
  );
}

TimelineItem.propTypes = {
  item: PropTypes.object.isRequired,
  isDark: PropTypes.bool.isRequired,
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function AnalyticsUsers({ filters, apiBase = '/admin/analytics' }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineItems, setTimelineItems] = useState([]);

  const fetchUsers = useCallback(() => {
    let cancelled = false;
    setLoading(true);

    const params = {
      search: search || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };

    const analyticsApi = makeAnalyticsApi(apiBase);
    analyticsApi.getUsers(params).then((d) => {
      if (!cancelled) {
        setRows(d.rows || []);
        setTotal(d.total || 0);
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [search, page, apiBase]);

  useEffect(() => {
    const cancel = fetchUsers();
    return cancel;
  }, [fetchUsers]);

  const handleSearchSubmit = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleRowClick = (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
    setTimelineLoading(true);
    setTimelineItems([]);

    const analyticsApi = makeAnalyticsApi(apiBase);
    analyticsApi.getUserTimeline(user.userId, { from: filters.from, to: filters.to }).then((d) => {
      setTimelineItems(d.items || []);
      setTimelineLoading(false);
    });
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Box>
      {/* ── Search bar ────────────────────────────────────────────────── */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by email, phone, or user ID…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
          sx={{ width: 320 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search
                  sx={{ cursor: 'pointer', fontSize: 18, opacity: 0.6 }}
                  onClick={handleSearchSubmit}
                />
              </InputAdornment>
            ),
          }}
        />
        <Typography
          variant="caption"
          sx={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(15,23,42,0.45)' }}
        >
          {total.toLocaleString()} users found
        </Typography>
      </Box>

      {/* ── Users table ───────────────────────────────────────────────── */}
      <GlassCard sx={{ p: 3 }}>
        {loading ? (
          <ShimmerSkeleton height={380} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No users found"
            description="No users match your search."
            iconIndex={0}
          />
        ) : (
          <>
            <GlassTable
              columns={[
                { label: 'User' },
                { label: 'Role' },
                { label: 'Last Seen', align: 'right' },
                { label: 'Sessions', align: 'right' },
                { label: 'Events', align: 'right' },
              ]}
            >
              {rows.map((row, i) => (
                <TableRow
                  key={i}
                  onClick={() => handleRowClick(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                        {row.email || row.phone || String(row.userId || '').slice(0, 16)}
                      </Typography>
                      {row.email && row.phone && (
                        <Typography
                          variant="caption"
                          sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(15,23,42,0.4)' }}
                        >
                          {row.phone}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.role || 'unknown'}
                      size="small"
                      sx={{
                        bgcolor: `${roleBadgeColor(row.role)}22`,
                        color: roleBadgeColor(row.role),
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 20,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">{fmtDate(row.lastSeen)}</TableCell>
                  <TableCell align="right">{(row.sessions || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{(row.events || 0).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </GlassTable>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </GlassCard>

      {/* ── User timeline dialog ───────────────────────────────────────── */}
      <GlassDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={
          selectedUser
            ? `${selectedUser.email || selectedUser.phone || 'User'} · Timeline`
            : 'User Timeline'
        }
        titleAction={
          selectedUser?.role && (
            <Chip
              label={selectedUser.role}
              size="small"
              sx={{
                bgcolor: `${roleBadgeColor(selectedUser?.role)}22`,
                color: roleBadgeColor(selectedUser?.role),
                fontWeight: 600,
                fontSize: '0.72rem',
              }}
            />
          )
        }
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {timelineLoading ? (
            <ShimmerSkeleton height={300} />
          ) : timelineItems.length === 0 ? (
            <EmptyState
              icon={<TimelineIcon />}
              title="No timeline events"
              description="This user has no recorded events in the selected period."
              iconIndex={1}
            />
          ) : (
            <Box>
              {timelineItems.map((item, i) => (
                <TimelineItem key={i} item={item} isDark={isDark} />
              ))}
            </Box>
          )}
        </DialogContent>
      </GlassDialog>
    </Box>
  );
}

AnalyticsUsers.propTypes = {
  filters: PropTypes.object.isRequired,
  apiBase: PropTypes.string,
};
